import { LOGIN_ROLES } from "@/lib/utils";
import { useProfilesByRoleQuery } from "@/services/employeeManagement/useProfilesByRoleQuery";
import { useGetLabResourcesBySchoolIdQuery } from "@/services/school/lab/useGetLabResourcesById";
import { useCreateLabResourcesMutation } from "@/services/school/lab/useAddLabResources";
import { useUpdateSchoolResourcesMutation } from "@/services/school/lab/useUpdateSchoolResources";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { useParams } from "react-router-dom";
import { FormValues, schema } from "./validations";
import { toast } from "sonner";

export const useAddEditResource = () => {
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(true);

  // 🧠 Fetch existing lab resources (for edit mode)
  const { data: labResourcesData, isLoading: isLoadingLabResources } =
    useGetLabResourcesBySchoolIdQuery(id || "", { enabled: !!id });

  const labResources = labResourcesData?.data || [];
  const isEditMode = labResources.length > 0;

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      resources: [
        {
          regionHeadId: "",
          relationshipManagerId: "",
          crmOpsStatus: "Green",
          comment: "",
          isDeleted: false,
        },
      ],
    },
  });

  const { control } = form;

  const { fields, append } = useFieldArray({
    control,
    name: "resources",
  });

  // 🧑‍🔬 Get lab team (regionHead options)
  const { data: labsteamData, isLoading: isloadinglabsteam } =
    useProfilesByRoleQuery(LOGIN_ROLES.LABS_TEAM);

  const labteamsOptions =
    labsteamData?.data?.[0]?.records?.map((profile: any) => ({
      label: profile.name,
      value: profile.profile_id,
    })) || [];

  const labsteamRecords = labsteamData?.data?.[0]?.records || [];

  // Mutations
  const { mutate: createLabResources, isPending: isCreatingLabResources } =
  useCreateLabResourcesMutation({
    onSuccess: () => {
      form.reset();
      toast.success("Lab Resources added successfully");
    },
  });

  const { mutate: updateLabResources, isPending: isUpdatingLabResources } =
  useUpdateSchoolResourcesMutation({
    onSuccess: () => {
      toast.success("Lab Resources updated successfully");
    },
  });

  const isSubmitting = isCreatingLabResources || isUpdatingLabResources;

  // ✨ Add new row
  const handleAddNewRow = () => {
    append({
      regionHeadId: "",
      relationshipManagerId: "",
      crmOpsStatus: "Green",
      comment: "",
      isDeleted: false,
    });
  };

  // ✨ Populate form with existing data
  useEffect(() => {
    if (labResources.length > 0) {
      const mapped = labResources.map((r: any) => ({
        regionHeadId: String(r.profile_id || ""),
        relationshipManagerId: "",
        crmOpsStatus: "Green" as const,
        comment: "",
        resourceId: r._id,
        isDeleted: false,
      }));
      form.reset({ resources: mapped });
    }
    setIsLoading(false);
  }, [labResources, form]);

  // 🧩 Submit handler (two separate payloads)
  const onSubmit = (values: FormValues) => {
  if (!id) return;

  const hasExistingResources = (values.resources || []).some(r => r.resourceId);

  if (hasExistingResources) {
    // UPDATE existing resources
    const updatePayload = {
      schoolId: id,
      resourceList: (values.resources || []).map((r: any) => ({
        ...(r.resourceId ? { resourceId: r.resourceId } : {}),
        profile_id: r.regionHeadId,
        isDeleted: !!r.isDeleted,
      })),
    };
    updateLabResources(updatePayload);
  } else {
    // CREATE new resources
    const addPayload = {
      schoolId: id,
      resourceList: (values.resources || [])
        .filter((r: any) => !r.isDeleted)
        .map((r: any) => ({
          profile_id: r.regionHeadId,
        })),
    };
    createLabResources(addPayload);
  }
};


  return {
    form,
    fields,
    handleAddNewRow,
    onSubmit,
    isSubmitting,
    labteamsOptions,
    labsteamRecords,
    isloadinglabsteam,
    regionHeadOptions: labteamsOptions,
    isLoadingRegionHeads: isloadinglabsteam,
    relationshipManagerOptions: [],
    isEditMode,
    isLoadingExisting:
      isLoadingLabResources || isLoading || (labteamsOptions?.length ?? 0) <= 0,
  };
};
